import { Col, Row } from "antd";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form from "../../form-components/from";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import {
  DonationStatus,
  DonationTarget,
  PeriodRatio,
  getDonationStatus,
  getDonationTarget,
  getPeriodRatio,
} from "../../../models/donation/enum";
import DonationContext from "../../../context/donation/context";
import EndPoints from "../../../services/end-points";

interface IProps {}

const DonationFilter: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(DonationContext);
  const [selectedTarget, setSelectedTarget] = useState<number | null>(null);
  const [targetItems, setTargetItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPeriodic, setIsPeriodic] = useState<boolean | null>(null);

  useEffect(() => {
    const fetchTargetItems = async () => {
      if (!selectedTarget) return;
      
      setLoading(true);
      try {
        let response;
        switch (selectedTarget) {
          case DonationTarget.CHARITY:
            response = await EndPoints.charity.getAllCharities({ light: true, perPage: -1 });
            setTargetItems(response.data.data.map((item: any) => ({
              label: item.name,
              value: item.id
            })));
            break;
          case DonationTarget.CAMPAIGN:
            response = await EndPoints.charity.getAllCharities({ light: true, perPage: -1 });
            setTargetItems(response.data.data.map((item: any) => ({
              label: item.name,
              value: item.id
            })));
            break;
          case DonationTarget.ORPHAN:
            response = await EndPoints.orphan.getAllOrphans({ light: true, perPage: -1 });
            setTargetItems(response.data.data.map((item: any) => ({
              label: `${item.firstName} ${item.lastName}`,
              value: item.id
            })));
            break;
        }
      } catch (error) {
        console.error('Error fetching target items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTargetItems();
  }, [selectedTarget]);

  return (
    <Form
      formId="donation-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: data?.keyword,
          filters: [
            data?.status
              ? {
                  name: "status",
                  value: data.status,
                  operation: "eq",
                }
              : undefined,
            data?.target
              ? {
                  name: "target",
                  value: data.target,
                  operation: "eq",
                }
              : undefined,
            data?.targetId && selectedTarget
              ? {
                  name: selectedTarget === DonationTarget.CHARITY 
                    ? "targetId" 
                    : selectedTarget === DonationTarget.CAMPAIGN 
                    ? "targetId" 
                    : "targetId",
                  value: data.targetId,
                  operation: "eq",
                }
              : undefined,
            isPeriodic === true && data?.periodRatio
              ? {
                  name: "periodRatio",
                  value: data.periodRatio,
                  operation: "eq",
                }
              : isPeriodic === false
              ? {
                  name: "periodRatio",
                  value: null,
                  operation: "eq",
                }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <Row justify={"start"} gutter={[10, 0]}>
        <Col span={8}>
          <FieldBuilder
            name="status"
            label={`${t("status")}`}
            width="large"
            input={{
              type: "select",
              options: Object.values(DonationStatus)
                .filter((value) => !isNaN(Number(value)))
                .map((status) => ({
                  label: t(getDonationStatus(status as DonationStatus)),
                  value: status as number,
                })),
            }}
          />
        </Col>
        <Col span={8}>
          <FieldBuilder
            name="target"
            label={`${t("target")}`}
            width="large"
            input={{
              type: "select",
              options: Object.values(DonationTarget)
                .filter((value) => !isNaN(Number(value)))
                .map((target) => ({
                  label: t(getDonationTarget(target as DonationTarget)),
                  value: target as number,
                })),
              onChange: (value) => setSelectedTarget(value),
            }}
          />
        </Col>
        {selectedTarget && (
          <Col span={8}>
            <FieldBuilder
              name="targetId"
              label={t(selectedTarget === DonationTarget.CHARITY 
                ? "charity" 
                : selectedTarget === DonationTarget.CAMPAIGN 
                ? "campaign" 
                : "orphan")}
              width="large"
              input={{
                type: "select",
                loading: loading,
                options: targetItems,
                allowSearch: true,
              }}
            />
          </Col>
        )}
        <Col span={8}>
          <FieldBuilder
            name="donationType"
            label={`${t("donation_type")}`}
            width="large"
            input={{
              type: "select",
              options: [
                { label: t("periodic"), value: true },
                { label: t("non_periodic"), value: false },
              ],
              onChange: (value) => setIsPeriodic(value),
            }}
          />
        </Col>
        {isPeriodic && (
          <Col span={8}>
            <FieldBuilder
              name="periodRatio"
              label={`${t("period_ratio")}`}
              width="large"
              input={{
                type: "select",
                options: Object.values(PeriodRatio)
                  .filter((value) => !isNaN(Number(value)))
                  .map((period) => ({
                    label: t(getPeriodRatio(period as PeriodRatio)),
                    value: period as number,
                  })),
              }}
            />
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default DonationFilter;